import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import type { EditorView } from '@codemirror/view';
import { Clock, FilePlus, FileUp, Folder, FolderOpen } from 'lucide-react';
import {
  extractHeadings,
  foldRegistry,
  isExternalLink,
  isWithinScopes,
  linkCandidates,
  outlineRegistry,
  type CodeSymbol,
  type FoldRange,
  type PlatformBridge,
  type RecentFile,
  type RecentWorkspace,
  type ToolHost,
  type WatchEvent,
} from '@slate/core';
import { useDocStore, loadSessionSnapshot } from '../store/docStore';
import { useFsDragStore } from '../store/fsDragStore';
import { formatSourcesAsMarkdown } from '../util/markdownInsert';
import {
  openFileToDoc,
  openPath,
  saveActiveDoc,
  saveAllDocs,
  saveDocById,
} from '../store/docActions';
import { supportsRichViews } from '../util/viewCapabilities';
import { effectiveFileName, langKeyForDoc } from '../util/docLanguage';
import { resolveSoftWrap } from '../util/softWrap';
import { workspaceLabel } from '../fs/containment';
import { ConflictDialog } from './ConflictDialog';
import { CloseConfirmDialog, type BusyTerminal } from './CloseConfirmDialog';
import { RecoveryDialog, type RecoveryChoice } from './RecoveryDialog';
import { attemptClose, type BusyTerminalInfo } from '../session/closeController';
import { draftRegistry } from '../store/draftId';
import { readManifest, writeManifest } from '../store/sessionManifest';
import { classifyTab } from '../session/recovery';
import { CloseAllWorkspacesDialog } from './CloseAllWorkspacesDialog';
import { AboutDialog } from './AboutDialog';
import { isMac, modPressed, shortcutLabel } from '../keymap/platform';
import { installShortcutReveal } from '../keymap/shortcutReveal';
import { useSystemTheme } from '../theme/useTheme';
import { useScrollSync } from '../hooks/useScrollSync';
import { useAutosave } from '../hooks/useAutosave';
import { useCrashSafeDrafts } from '../hooks/useCrashSafeDrafts';
import { useSessionPersistence } from '../hooks/useSessionPersistence';
import {
  editorTopLine,
  previewTopSlug,
  placeCursorAtLine,
  scrollWysiwygToHeadingIndexSmooth,
  wysiwygTopHeadingIndex,
} from '../scroll/scrollMap';
import { Toolbar } from './Toolbar';
import { TitleBar } from './TitleBar';
import { ActivityBar } from '../sidebar/ActivityBar';
import { SidebarHost } from '../sidebar/SidebarHost';
import { useSidebarStore } from '../sidebar/sidebarStore';
import { useMainViews } from '../mainview/useMainViews';
import { registerBuiltinSidebarPanels } from '../sidebar/registerBuiltinPanels';
import { registerPluginsSidebarPanel, getAppPluginSystem } from '../plugins/appPlugins';
import { registerPluginContributedPanels } from '../plugins/pluginActivityBar';
import { registerPluginContributedTools } from '../tools/pluginToolBridge';
import { registerGitMainView } from '../git/client/registerGitMainView';
import { GIT_PLUGIN_ID } from '../plugins/builtinGit';
import { makeSidebarFileActions } from '../sidebar/sidebarActions';
import { OutlinePanel, type OutlineHeading } from './OutlinePanel';
import { CodeOutlinePanel } from './CodeOutlinePanel';
import '../builtins/outlineProvider';
import { PlainTextEditor } from './PlainTextEditor';
import { ImageViewer } from './ImageViewer';
import { RenderedPreview, type RenderedPreviewHandle } from './RenderedPreview';
import { ReadingControls } from './ReadingControls';
import { SplitView } from './SplitView';
import { isTauriEnv, useTauriChrome } from './useTauriEnv';
import { ContextMenuProvider } from '../context-menu';
import { ToolPanel } from '../tools/ToolPanel';
import { ToolRegistry } from '../tools/ToolRegistry';
import { terminalToolDefinition } from '../tools/terminal/definition';
import { useToolsStore, toolsFromSnapshot } from '../tools/toolsStore';
import {
  useFocusZoneShortcuts,
  useFocusZoneAttributes,
  useRegisterFocusZone,
} from '../focus';
import { CommandPalette } from '../palette';
import { useRegisterToolKeybindings } from '../keymap/registerToolContributions';
import { WELCOME_TIPS } from '../welcome/tips';
import { RecentsList, type RecentItemVM } from '../welcome/RecentsList';
import { ToastHost } from './ToastHost';
import { ImageLightbox } from './ImageLightbox';
import { EditorContextArea } from './EditorContextArea';
import { UpdateInstallModal } from './UpdateInstallModal';
import { UpdateConfirmDialog } from './UpdateConfirmDialog';
import { toast, useToastStore } from '../store/toastStore';
import {
  useUpdateStore,
  START_UPDATE_EVENT,
  OPEN_UPDATE_GATE_EVENT,
} from '../store/updateStore';

const WysiwygEditor = lazy(() =>
  import('./WysiwygEditor').then((m) => ({ default: m.WysiwygEditor })),
);
// Imperative-handle type imported eagerly (type-only, no bundle cost).
import type { WysiwygEditorHandle } from './WysiwygEditor';

export interface AppProps {
  platform: PlatformBridge;
}

export function App({ platform }: AppProps): ReactElement {
  useTauriChrome();
  // Register the built-in terminal tool exactly once (module-singleton
  // registry). Wire the global focus-zone shortcuts (⌥⌘+Arrow / F6) and the
  // CSS attribute feedback for the current zone.
  useEffect(() => {
    if (!ToolRegistry.getById('terminal')) {
      ToolRegistry.register(terminalToolDefinition);
    }
  }, []);
  // "Shortcut reveal" mode: holding the platform command modifier alone for ~1s
  // surfaces top-right shortcut badges on the chrome controls. Self-contained
  // window listeners; SSR/jsdom-safe (the installer no-ops without `window`).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    return installShortcutReveal();
  }, []);
  useFocusZoneShortcuts();
  useFocusZoneAttributes();
  const dark = useSystemTheme();
  const docs = useDocStore((s) => s.docs);
  const activeId = useDocStore((s) => s.activeId);
  const viewMode = useDocStore((s) => s.viewMode);
  const split = useDocStore((s) => s.split);
  const setText = useDocStore((s) => s.setText);
  const newUntitled = useDocStore((s) => s.newUntitled);
  const openDoc = useDocStore((s) => s.openDoc);
  const workspaceRoots = useDocStore((s) => s.workspaceRoots);
  const outlinePinned = useDocStore((s) => s.outlinePinned);
  const toggleOutlinePinned = useDocStore((s) => s.toggleOutlinePinned);
  const readingFontScale = useDocStore((s) => s.readingFontScale);
  const readingWidth = useDocStore((s) => s.readingWidth);
  const softWrapDefault = useDocStore((s) => s.softWrapDefault);
  const softWrapExcludeCode = useDocStore((s) => s.softWrapExcludeCode);
  const softWrapByLanguage = useDocStore((s) => s.softWrapByLanguage);
  const pendingJumpLine = useDocStore((s) => s.pendingJumpLine);
  const setPendingJumpLine = useDocStore((s) => s.setPendingJumpLine);
  const conflicts = useDocStore((s) => s.conflicts);
  const closeQueue = useDocStore((s) => s.closeQueue);

  // A plugin-contributed main view (e.g. the Git client) can replace the whole
  // editor area. It is selected from the activity bar and persisted in the
  // sidebar store. `activeMainViewItem` is null when nothing is selected OR the
  // selected view's plugin is disabled (its registration is gone) — in which
  // case we fall through to the normal editor area rather than crash.
  const activeMainView = useSidebarStore((s) => s.activeMainView);
  const mainViews = useMainViews();
  const activeMainViewItem = activeMainView
    ? (mainViews.find((v) => v.id === activeMainView) ?? null)
    : null;
  const mainViewActive = activeMainViewItem != null;

  // The native traffic-light buttons only exist on macOS, and only under Tauri
  // (the browser has no native window chrome). On Linux/Windows the app draws
  // its own controls on the RIGHT, so reserving the left inset there just pushes
  // the logo/header content off the left edge — gate it to macOS so the chrome
  // sits flush-left everywhere else.
  const trafficLights = isTauriEnv() && isMac();

  // New / Open File / Open Folder actions for the activity-bar rail (shared with
  // the Explorer empty state via the same factory).
  const sidebarActions = useMemo(() => makeSidebarFileActions(platform), [platform]);

  const addWorkspaceRoot = useDocStore((s) => s.addWorkspaceRoot);
  const removeWorkspaceRoot = useDocStore((s) => s.removeWorkspaceRoot);
  const [closeAllWsOpen, setCloseAllWsOpen] = useState(false);
  const requestCloseAllWorkspaces = useCallback(() => {
    const roots = useDocStore.getState().workspaceRoots;
    if (roots.length === 0) return;
    setCloseAllWsOpen(true);
  }, []);
  const confirmCloseAllWorkspaces = useCallback(
    (ids: string[]) => {
      for (const id of ids) removeWorkspaceRoot(id);
      setCloseAllWsOpen(false);
    },
    [removeWorkspaceRoot],
  );

  const activeDoc = docs.find((d) => d.id === activeId);
  const text = activeDoc?.text ?? '';

  // Keep the window/tab title in sync with the active document: "Slate" when
  // nothing is open, "<name> — Slate" for an active doc (its file name, or
  // "Untitled" for an unsaved buffer), prefixed with "• " while it has unsaved
  // edits. Guarded for SSR (no `document`).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!activeDoc) {
      document.title = 'Slate';
      return;
    }
    const name = activeDoc.ref?.name ?? 'Untitled';
    document.title = `${activeDoc.dirty ? '• ' : ''}${name} — Slate`;
  }, [activeDoc?.id, activeDoc?.ref?.name, activeDoc?.dirty, activeDoc]);

  // An IMAGE doc is a read-only viewer (no editor, no toolbar, no outline).
  const isViewerOnly = activeDoc?.kind === 'image';

  // A NON-markdown doc (code/text/plain) opens in the plain-text editor ONLY —
  // no WYSIWYG/Reading/Split. The "language" a doc behaves as is its EFFECTIVE
  // filename: a real `ref.name`, a `/lang` override's extension, or the Plain
  // Text default (`untitled.txt`) for an untitled buffer with no override. So an
  // untitled buffer is plain text until `/lang markdown` (decision A), and a
  // `/lang typescript` override drives code highlighting. Image docs are handled
  // separately (viewer-only), never as the editor.
  const effectiveName = activeDoc ? effectiveFileName(activeDoc) : null;
  const softWrap = activeDoc
    ? resolveSoftWrap(langKeyForDoc(activeDoc), activeDoc.wrapOverride, {
        softWrapDefault,
        softWrapExcludeCode,
        softWrapByLanguage,
      })
    : false;
  const editorOnly =
    !isViewerOnly && effectiveName != null && !supportsRichViews(effectiveName);

  // Adaptive chrome:
  //  - No document open → only the welcome/empty state (no toolbar, no outline).
  //  - A markdown / untitled doc → full chrome (view controls, split, outline).
  //  - A code/text file → toolbar shows Save only; outline + view controls hide.
  //  - An image doc → read-only viewer: no toolbar, no view controls, no outline.
  const hasDoc = docs.length > 0;
  const hasMarkdownDoc = hasDoc && !editorOnly && !isViewerOnly;

  // Code outline: when the active doc is a supported code file, we parse it
  // (off-thread) into a symbol hierarchy instead of showing markdown headings.
  const outlineProvider =
    editorOnly && effectiveName ? outlineRegistry.providerForFile(effectiveName) : null;
  const hasCodeOutline = outlineProvider != null;
  const [codeSymbols, setCodeSymbols] = useState<CodeSymbol[]>([]);

  // Empty-state actions (rendered when no documents are open).
  const onEmptyNewFile = useCallback(() => {
    newUntitled();
  }, [newUntitled]);
  const onEmptyOpenFile = useCallback(() => {
    void openFileToDoc(platform);
  }, [platform]);
  const onEmptyOpenFolder = useCallback(() => {
    void (async () => {
      try {
        const ref = await platform.openFolderDialog();
        if (ref) {
          addWorkspaceRoot(ref);
          void platform.pushRecentWorkspace(ref).catch(() => {});
        }
      } catch (err) {
        console.debug('[slate] open-folder failed', err);
      }
    })();
  }, [platform, addWorkspaceRoot]);

  // Recent files / workspaces for the welcome screen. Loaded each time the
  // welcome state appears (re-loaded when the platform changes — e.g. cross-mode
  // test). The store keeps up to 100 each; the welcome list (RecentsList) caps
  // the *display* at 10. The palette surfaces the full list.
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>([]);
  const [recentWorkspaces, setRecentWorkspaces] = useState<RecentWorkspace[]>([]);
  const showWelcome = !hasDoc;
  // A random tip picked once per welcome appearance. Recomputing on every
  // welcome mount (showWelcome flipping true) feels alive without flickering
  // mid-session.
  const [welcomeTip, setWelcomeTip] = useState<string>(() => WELCOME_TIPS[0]);
  useEffect(() => {
    if (!showWelcome) return;
    setWelcomeTip(WELCOME_TIPS[Math.floor(Math.random() * WELCOME_TIPS.length)]);
    let cancelled = false;
    void Promise.all([
      platform.recentFiles().catch(() => [] as RecentFile[]),
      platform.recentWorkspaces().catch(() => [] as RecentWorkspace[]),
    ]).then(([files, workspaces]) => {
      if (cancelled) return;
      setRecentFiles(files);
      setRecentWorkspaces(workspaces);
    });
    return () => {
      cancelled = true;
    };
  }, [platform, showWelcome]);

  const onOpenRecentFile = useCallback(
    (item: RecentItemVM) => {
      if (!item.path) return;
      void openPath(platform, item.path);
    },
    [platform],
  );
  const onOpenRecentWorkspace = useCallback(
    (item: RecentItemVM) => {
      if (!item.path) return;
      const ref = { id: item.id, path: item.path, name: item.name };
      addWorkspaceRoot(ref);
      void platform.pushRecentWorkspace(ref).catch(() => {});
    },
    [addWorkspaceRoot, platform],
  );
  // Remove a single entry from recents: optimistically drop it from the visible
  // list, then persist the removal best-effort (the bridge method is optional).
  const onRemoveRecentFile = useCallback(
    (id: string) => {
      setRecentFiles((prev) => prev.filter((f) => f.ref.id !== id));
      void platform.removeRecentFile?.(id).catch(() => {});
    },
    [platform],
  );
  const onRemoveRecentWorkspace = useCallback(
    (id: string) => {
      setRecentWorkspaces((prev) => prev.filter((w) => w.ref.id !== id));
      void platform.removeRecentWorkspace?.(id).catch(() => {});
    },
    [platform],
  );

  const [editorScroller, setEditorScroller] = useState<HTMLElement | null>(null);
  const [previewScroller, setPreviewScroller] = useState<HTMLElement | null>(null);
  // Imperative handle into the virtualized preview — needed so anchor links
  // and source-line jumps can target blocks that aren't currently mounted
  // (phase 4b). The previewScroller above is still used by hover/scroll-sync
  // logic that only cares about the scroll container element.
  const previewHandleRef = useRef<RenderedPreviewHandle | null>(null);
  const [editorView, setEditorView] = useState<EditorView | null>(null);
  // Imperative handle to the WYSIWYG view — used by the sidebar-drag drop
  // handler to splice in a markdown link/image at the cursor.
  const wysiwygHandleRef = useRef<WysiwygEditorHandle | null>(null);
  // Viewport caret rect painted while a sidebar drag hovers an editor
  // surface — previews where the markdown link/image will land on drop.
  const [dropCaret, setDropCaret] = useState<{
    left: number;
    top: number;
    bottom: number;
  } | null>(null);
  // Lightbox state — { src, name } when an inline preview image is open.
  const [lightbox, setLightbox] = useState<{ src: string; name: string } | null>(null);
  // Whether the (unpinned) outline overlay is currently revealed by edge-hover.
  const [outlineRevealed, setOutlineRevealed] = useState(false);
  // Slug of the heading nearest the top of the active view (outline scroll-spy).
  const [activeHeadingSlug, setActiveHeadingSlug] = useState<string | null>(null);

  // The editor area is a focus zone (⌥⌘↑ / F6 cycle land here). The CodeMirror
  // content (or any opted-in `[data-focus-primary]`) is the focus target.
  const editorZoneRef = useRef<HTMLDivElement>(null);
  useRegisterFocusZone(editorZoneRef, 'editor', '.cm-content, [data-focus-primary]', {
    label: 'Editor',
    order: 20,
    isVisible: () => true,
  });

  // Host handed to every mounted tool (terminal etc.). Reads live document /
  // workspace state imperatively so a tool always sees the current context,
  // and bridges notifications to the toast deck.
  const toolHost: ToolHost = useMemo(
    () => ({
      platform,
      getActiveDocPath: () => {
        const s = useDocStore.getState();
        const active = s.docs.find((d) => d.id === s.activeId);
        return active?.ref?.path ?? null;
      },
      getWorkspaceRoots: () =>
        useDocStore.getState().workspaceRoots.map((ref) => ({ rootId: ref.id, ref })),
      getTheme: () => (dark ? 'dark' : 'light'),
      notify: (level, message) =>
        toast(message, { kind: level === 'error' ? 'error' : 'info' }),
      openTool: (toolId, initialState) =>
        useToolsStore.getState().openTool(toolId, initialState),
    }),
    [platform, dark],
  );

  // Bind every registered tool's `commands` keybindings at the window level
  // (e.g. terminal's ⌘` toggle / ⇧⌘` new). The registry owns these bindings —
  // see `terminalToolDefinition.contributions.commands`. Rebinds live as tools
  // register/unregister.
  useRegisterToolKeybindings(toolHost);

  const editorVisible = viewMode !== 'rendered';
  // Preview shows in the rendered view, or in split (editor + preview).
  const previewVisible = !editorVisible || split;

  // The outline is a TRUE toggle of the persisted `outlinePinned` flag. We also
  // clear the transient hover-reveal so a click that unpins reliably closes the
  // panel even if the cursor is still over it (its onMouseLeave hasn't fired).
  const handleToggleOutline = useCallback(() => {
    setOutlineRevealed(false);
    toggleOutlinePinned();
  }, [toggleOutlinePinned]);

  // The bottom tool-panel shortcuts (⌘` toggle panel, ⇧⌘` new terminal) are
  // now owned by the tool-contribution registry (`terminal.toggle` /
  // `terminal.new`) and bound via `useRegisterToolKeybindings` above — no
  // hand-written handler here.

  useScrollSync({
    previewEl: previewScroller,
    previewHandle: previewHandleRef.current,
    editorEl: editorScroller,
    editorKind: viewMode === 'wysiwyg' ? 'wysiwyg' : 'plaintext',
    editorView,
    enabled: split && viewMode !== 'rendered',
  });

  // Debounced autosave (active only when the persisted `autosave` flag is on).
  // Saves dirty file-backed docs ~5s after the last edit (30s max-wait); flushes
  // on tab switch / unmount / disable. Untitled buffers are never auto-saved.
  useAutosave(platform);
  useCrashSafeDrafts(platform);
  useSessionPersistence(platform);

  // Outline heading tree (derived from the markdown string only — cheap, and
  // works without any DOM, so jsdom tests are fine). Tag each with its flat
  // document index for WYSIWYG heading-index jumps.
  const headings = useMemo<OutlineHeading[]>(
    () => extractHeadings(text).map((h, index) => ({ ...h, index })),
    [text],
  );

  // Re-extract the code outline ~150ms after the last edit. Guarded by a token so
  // a stale parse can never overwrite a newer one (or a different document).
  useEffect(() => {
    if (!outlineProvider || !effectiveName) {
      setCodeSymbols([]);
      return;
    }
    let cancelled = false;
    const provider = outlineProvider;
    const name = effectiveName;
    const handle = setTimeout(() => {
      void provider
        .getSymbols(text, name)
        .then((syms) => {
          if (!cancelled) setCodeSymbols(syms);
        })
        .catch(() => {
          if (!cancelled) setCodeSymbols([]);
        });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [text, outlineProvider, effectiveName]);

  // --- Code folding ---------------------------------------------------------
  // Fold ranges for the plain-text editor. Mirrors the code-outline fetch:
  // providers come from the core fold registry (the first-party Slate Coder
  // plugin contributes markdown-heading, code-symbol, and indentation providers;
  // any plugin can add more). They are pulled ~150ms after the last edit, then
  // merged and deduped by start line (widest span wins). A registry subscription
  // bumps `foldRev` so enabling/disabling a fold-provider plugin re-derives.
  const [foldRanges, setFoldRanges] = useState<FoldRange[]>([]);
  const [foldRev, setFoldRev] = useState(0);
  useEffect(() => foldRegistry.subscribe(() => setFoldRev((n) => n + 1)), []);
  useEffect(() => {
    if (!effectiveName) {
      setFoldRanges([]);
      return;
    }
    const name = effectiveName;
    const providers = foldRegistry.providersForFile(name);
    if (providers.length === 0) {
      setFoldRanges([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      void Promise.all(
        providers.map((p) => p.getFolds(text, name).catch(() => [] as FoldRange[])),
      ).then((results) => {
        if (cancelled) return;
        const byStart = new Map<number, FoldRange>();
        for (const list of results) {
          for (const r of list) {
            if (r.endLine <= r.startLine) continue;
            const cur = byStart.get(r.startLine);
            if (!cur || r.endLine > cur.endLine) byStart.set(r.startLine, r);
          }
        }
        setFoldRanges(
          [...byStart.values()].sort(
            (a, b) => a.startLine - b.startLine || a.endLine - b.endLine,
          ),
        );
      });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [text, effectiveName, foldRev]);
  // Folding is owned by a fold-provider plugin (e.g. Slate Coder). When no
  // provider matches the current file (plugin disabled, or unsupported type)
  // folding must be fully OFF, not just emptied of ranges. Derived from the SAME
  // inputs as the fetch above so enabled-state and ranges never disagree.
  const foldingEnabled = useMemo(
    () => (effectiveName ? foldRegistry.providersForFile(effectiveName).length > 0 : false),
    [effectiveName, foldRev],
  );
  // --- end Code folding -----------------------------------------------------

  // Click-to-jump: scroll EVERY currently-visible pane to the heading. Preview
  // by slug (#id), plaintext via the captured EditorView + source line, WYSIWYG
  // by heading index (ProseMirror has no slug/line).
  const jumpToHeading = useCallback(
    (heading: OutlineHeading) => {
      // Optimistically light up the clicked item; the scroll-spy will keep it in
      // sync once the (smooth) scroll settles.
      setActiveHeadingSlug(heading.slug);
      if (previewVisible) previewHandleRef.current?.scrollToSlug(heading.slug);
      if (editorVisible) {
        if (viewMode === 'wysiwyg') {
          scrollWysiwygToHeadingIndexSmooth(editorScroller, heading.index);
        } else {
          // Plaintext: place the caret at the heading line and focus the editor.
          placeCursorAtLine(editorView, heading.line);
        }
      }
    },
    [previewVisible, editorVisible, viewMode, previewScroller, editorScroller, editorView],
  );

  // Code-outline click-to-jump: place the caret at the symbol's line AND focus
  // the editor, so the user can start editing right there (not just scroll).
  const jumpToSymbol = useCallback(
    (line: number) => {
      placeCursorAtLine(editorView, line);
    },
    [editorView],
  );

  // Outline scroll-spy: highlight the heading nearest the top of the ACTIVE
  // view as the user scrolls. We read the topmost heading from whichever
  // scroller drives the current mode (preview when visible; otherwise the
  // editor) and map it to a slug. rAF-throttled and fully guarded so it never
  // throws (jsdom has no real layout — the listener simply yields null/no-op).
  useEffect(() => {
    // Pick the scroller to watch + how to derive the active slug from it.
    const watchPreview = previewVisible;
    const scroller = watchPreview ? previewScroller : editorScroller;
    if (!scroller) {
      setActiveHeadingSlug(null);
      return;
    }

    const computeSlug = (): string | null => {
      if (watchPreview) return previewTopSlug(scroller);
      if (viewMode === 'wysiwyg') {
        const idx = wysiwygTopHeadingIndex(scroller);
        return idx >= 0 ? headings[idx]?.slug ?? null : null;
      }
      // Plaintext: nearest heading whose source line is at/above the top line.
      const topLine = editorTopLine(editorView);
      let slug: string | null = null;
      for (const h of headings) {
        if (h.line <= topLine) slug = h.slug;
        else break;
      }
      return slug;
    };

    let frame = 0;
    const update = (): void => {
      frame = 0;
      try {
        setActiveHeadingSlug(computeSlug());
      } catch {
        /* never throw out of a scroll handler */
      }
    };
    const onScroll = (): void => {
      if (frame !== 0) return;
      frame =
        typeof requestAnimationFrame === 'function' ? requestAnimationFrame(update) : 0;
      if (frame === 0) update();
    };

    // Initial sync (after a frame so a freshly-mounted view can lay out).
    onScroll();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame !== 0 && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frame);
      }
    };
  }, [previewVisible, previewScroller, editorScroller, editorView, viewMode, headings]);

  // Resolve a clicked hyperlink in the rendered preview. External links open in
  // the OS browser; in-page anchors scroll the preview; internal/relative links
  // resolve to a local file (doc-dir first, then workspace root) and open it.
  // Fully guarded — never throws into render.
  const handleLink = useCallback(
    (href: string): void => {
      void (async () => {
        try {
          if (isExternalLink(href)) {
            const trimmed = href.trim();
            // In-page anchor (#id) → scroll the preview to that heading.
            if (trimmed.startsWith('#')) {
              previewHandleRef.current?.scrollToSlug(trimmed.slice(1));
              return;
            }
            // Defense-in-depth: only hand http/https/mailto to the OS handler,
            // parsed independently — do NOT trust the sanitizer for this
            // OS-handoff boundary. Drop file:/ftp:/vbscript:/tel:/etc.
            let scheme = '';
            try {
              scheme = new URL(trimmed).protocol.toLowerCase();
            } catch {
              scheme = '';
            }
            if (scheme === 'http:' || scheme === 'https:' || scheme === 'mailto:') {
              if (typeof platform.openExternal === 'function') {
                await platform.openExternal(trimmed);
              }
            } else {
              console.debug('[slate] blocked non-allowlisted link scheme', href);
            }
            return;
          }

          // Internal / relative: try each candidate path until one exists AND
          // passes the safety scope. Span all workspace roots — doc-relative
          // candidates come first and dedupe naturally across roots (first-seen
          // order preserved). Desktop paths are absolute; web paths are
          // synthetic ids sharing a `webdir/<n>/<root>` prefix — both compare
          // correctly under isWithinScopes.
          if (typeof platform.pathExists !== 'function') return;
          const current = useDocStore.getState().activeDoc();
          const docPath = current?.ref?.path;

          // Allowed scopes (spec §7): the active document's DIRECTORY plus every
          // opened workspace root. Only a candidate that is one of these or a
          // descendant may be probed/opened — blocking `..` escapes and absolute
          // paths pointing elsewhere.
          const scopes: string[] = [];
          if (docPath) {
            const posix = docPath.replace(/\\/g, '/');
            const slash = posix.lastIndexOf('/');
            if (slash > 0) scopes.push(posix.slice(0, slash));
            else if (slash === 0) scopes.push('/');
          }
          for (const root of workspaceRoots) {
            // Web roots use `path === id`; fall back to `id` when path is absent.
            const rootPath = root.path ?? root.id;
            if (rootPath) scopes.push(rootPath);
          }

          let candidates: string[];
          if (workspaceRoots.length === 0) {
            candidates = linkCandidates(href, { docPath });
          } else {
            const seen = new Set<string>();
            candidates = [];
            for (const root of workspaceRoots) {
              const workspaceRoot = root.path ?? root.id;
              for (const c of linkCandidates(href, { docPath, workspaceRoot })) {
                if (seen.has(c)) continue;
                seen.add(c);
                candidates.push(c);
              }
            }
          }
          for (const p of candidates) {
            // Refuse anything outside the allowed scopes before touching the FS.
            if (!isWithinScopes(p, scopes)) continue;
            if (await platform.pathExists(p)) {
              const { ref, content } = await platform.readFile(p);
              openDoc(ref, content);
              return;
            }
          }
          if (candidates.length > 0) {
            console.debug('[slate] no in-scope local file found for link', href, candidates);
          }
        } catch (err) {
          console.debug('[slate] link resolution failed', href, err);
        }
      })();
    },
    [platform, previewScroller, workspaceRoots, openDoc],
  );

  // Copy text to the OS clipboard via the platform bridge (used by the preview
  // context menu's Copy / Copy link). Guarded — never throws into render.
  const copyText = useCallback(
    (value: string): void => {
      void (async () => {
        try {
          await platform.writeClipboardText(value);
        } catch (err) {
          console.debug('[slate] clipboard write failed', err);
        }
      })();
    },
    [platform],
  );

  // Seed: restore the previous session from the native session store (workspace
  // roots + open tabs + active tab + tool panel), falling back to a one-time
  // migration of the legacy localStorage snapshot. When nothing is restored the
  // app lands on the welcome / empty state — NOTHING auto-opens. Untitled
  // buffers are restored verbatim from their drafts (marked dirty); file-backed
  // tabs open from disk.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let manifest = await readManifest(platform);
      let didMigrate = false;

      // One-time migration from the legacy localStorage snapshot.
      if (!manifest) {
        const legacy = loadSessionSnapshot();
        if (legacy) {
          manifest = {
            version: 1,
            workspaceRoots: legacy.workspaceRoots,
            tabs: legacy.openDocs.map((d) => ({
              id: d.id,
              kind: 'file' as const,
              path: d.path,
              name: d.name,
              dirty: false,
            })),
            activeId: legacy.activeId,
            tools: legacy.toolsSnapshot ?? { panel: { visible: false, height: 260 }, instances: [], activeInstanceId: null },
          };
          didMigrate = true;
        }
      }

      if (manifest) {
        const store = useDocStore.getState();
        for (const root of manifest.workspaceRoots) {
          if (!root.path) continue;
          store.addWorkspaceRoot({ id: root.id, path: root.path, name: root.name });
        }
        const conflicts: Array<{ tab: typeof manifest.tabs[number]; draftText: string }> = [];
        for (const tab of manifest.tabs) {
          if (cancelled) return;
          if (tab.kind === 'untitled') {
            // Restore the unsaved scratch buffer verbatim from its draft.
            const text = tab.draftId
              ? (await platform.session?.readDraft(tab.draftId).catch(() => null)) ?? ''
              : '';
            const newId = useDocStore.getState().newUntitled(text);
            useDocStore.setState((s) => ({
              docs: s.docs.map((d) => (d.id === newId ? { ...d, dirty: !!tab.draftId } : d)),
            }));
            if (tab.langOverride) {
              useDocStore.getState().setDocLanguage(newId, tab.langOverride);
            }
            if (tab.draftId) draftRegistry.assign(newId, tab.draftId);
          } else if (tab.path) {
            let diskText: string | null = null;
            try {
              const opened = await platform.readFile({ id: tab.id, path: tab.path, name: tab.name });
              diskText = opened.content;
            } catch {
              diskText = null; // missing on disk
            }
            if (cancelled) return;
            const draftText = tab.draftId
              ? await platform.session?.readDraft(tab.draftId).catch(() => null) ?? null
              : null;
            const cls = classifyTab({ kind: 'file', draftText, diskText });
            if (cls === 'open-disk') {
              if (diskText != null) {
                useDocStore.getState().openDoc({ id: tab.id, path: tab.path, name: tab.name }, diskText);
              }
              if (tab.draftId) void platform.session?.deleteDraft(tab.draftId).catch(() => {});
            } else if (cls === 'restore-missing') {
              const id = useDocStore.getState().openDoc(
                { id: tab.id, path: tab.path, name: tab.name },
                draftText ?? '',
              );
              useDocStore.setState((s) => ({
                docs: s.docs.map((d) => (d.id === id ? { ...d, dirty: true } : d)),
              }));
              if (tab.draftId) draftRegistry.assign(id, tab.draftId);
            } else if (cls === 'conflict') {
              // Defer: open from disk now so the tab exists; the dialog (below)
              // will switch it to the draft if the user picks Restore.
              if (diskText != null) {
                useDocStore.getState().openDoc({ id: tab.id, path: tab.path, name: tab.name }, diskText);
              }
              conflicts.push({ tab, draftText: draftText ?? '' });
            }
            // Re-apply the persisted language override to whichever tab the
            // restore opened (its id is `tab.id`). Safe no-op if the doc
            // wasn't opened (e.g. a discarded conflict).
            if (tab.langOverride) {
              useDocStore.getState().setDocLanguage(tab.id, tab.langOverride);
            }
          }
        }
        if (manifest.activeId) {
          const present = useDocStore.getState().docs.some((d) => d.id === manifest.activeId);
          if (present) useDocStore.getState().activateDoc(manifest.activeId);
        }
        toolsFromSnapshot(manifest.tools);

        if (conflicts.length > 0 && !cancelled) {
          setRecoveryItems(
            conflicts.map((c) => ({ id: c.tab.id, name: c.tab.name, path: c.tab.path! })),
          );
          recoveryDraftsRef.current = Object.fromEntries(
            conflicts.map((c) => [c.tab.id, { draftText: c.draftText, draftId: c.tab.draftId }]),
          );
          setRecoveryOpen(true);
        }

        if (didMigrate && !cancelled) {
          // Persist the migrated session to the native store BEFORE dropping the
          // legacy key, so a crash in between can still re-migrate next launch.
          await writeManifest(platform);
          try {
            globalThis.localStorage?.removeItem('slate:session:v1');
          } catch {
            /* ignore */
          }
        }
      }

      // Nothing to auto-open: a launch with no restorable session lands on the
      // welcome / empty state (rendered when `docs.length === 0`).
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag-drop onto the window: a dropped FILE opens as a document; a dropped
  // FOLDER is added as a workspace root (reusing existing folder support). The
  // adapter hands us platform paths/synthetic ids; we route each via
  // isDirectory. Fully guarded — a single bad path never throws or aborts the rest.
  useEffect(() => {
    if (!platform.capabilities.dragDrop || typeof platform.onFileDrop !== 'function') {
      return;
    }
    let unsub: (() => void) | undefined;
    let cancelled = false;

    const handleDrop = (paths: string[]): void => {
      void (async () => {
        for (const path of paths) {
          try {
            const isDir = (await platform.isDirectory?.(path)) ?? false;
            if (isDir) {
              const name = workspaceLabel(path);
              const ref = { id: path, path, name };
              addWorkspaceRoot(ref);
              void platform.pushRecentWorkspace(ref).catch(() => {});
            } else {
              // Routes on type: an image opens in the viewer, else as text.
              await openPath(platform, path);
            }
          } catch (err) {
            console.debug('[slate] drop handling failed for', path, err);
          }
        }
      })();
    };

    void platform
      .onFileDrop(handleDrop)
      .then((u) => {
        if (cancelled) u();
        else unsub = u;
      })
      .catch((err) => console.debug('[slate] onFileDrop registration failed', err));

    return () => {
      cancelled = true;
      try {
        unsub?.();
      } catch {
        /* ignore unsubscribe errors */
      }
    };
  }, [platform, addWorkspaceRoot]);

  // Sidebar → editor drag: when the user drags a tree row and releases over
  // the plaintext or WYSIWYG editor, splice in a markdown link/image at the
  // drop position. Uses the same onDropFiles bridge the sidebar uses; the
  // shared useFsDragStore carries the source paths from the sidebar's
  // dragstart so this handler can read them regardless of which subscriber
  // fires first.
  useEffect(() => {
    if (typeof platform.onDropFiles !== 'function') return;
    let unsub: (() => void) | null = null;
    let cancelled = false;
    /**
     * Resolve the would-be caret rect for the editor under cursor (or null
     * when the cursor is not over an editor surface OR no in-app drag is
     * in flight). Shared by onDrag + onDrop so the visible caret and the
     * actual insertion target stay consistent.
     */
    const caretAt = (
      x: number,
      y: number,
    ): { rect: { left: number; top: number; bottom: number }; isWysiwyg: boolean } | null => {
      if (typeof document === 'undefined') return null;
      const el = document.elementFromPoint(x, y);
      const editorEl = el?.closest('.mp-editor') as HTMLElement | null;
      if (!editorEl) return null;
      if (editorEl.classList.contains('mp-wysiwyg')) {
        const rect = wysiwygHandleRef.current?.caretRectAt(x, y);
        return rect ? { rect, isWysiwyg: true } : null;
      }
      const view = editorView;
      if (!view) return null;
      const pos = view.posAtCoords({ x, y });
      if (pos == null) return null;
      try {
        const c = view.coordsAtPos(pos);
        if (!c) return null;
        return { rect: { left: c.left, top: c.top, bottom: c.bottom }, isWysiwyg: false };
      } catch {
        return null;
      }
    };
    platform
      .onDropFiles({
        onDrag: (e) => {
          // Only show the caret for in-app drags (paths empty) when a real
          // source set is tracked. OS file drags shouldn't paint a caret in
          // the editor — they're routed by the sidebar / App's open path.
          if ((e.paths?.length ?? 0) > 0) {
            setDropCaret(null);
            return;
          }
          if (useFsDragStore.getState().sources.length === 0) {
            setDropCaret(null);
            return;
          }
          const hit = caretAt(e.position.x, e.position.y);
          setDropCaret(hit ? hit.rect : null);
        },
        onLeave: () => setDropCaret(null),
        onDrop: (e) => {
          // OS file drops are not for the editor — they're handled by the
          // sidebar onDropFiles subscription (paste-into-folder) or by the
          // App-level onFileDrop (open file). We only act on in-webview
          // drags that originated in the sidebar (paths empty, sources in
          // the shared store).
          if (e.paths.length > 0) return;
          const sources = useFsDragStore.getState().sources;
          if (sources.length === 0) return;
          setDropCaret(null);
          if (typeof document === 'undefined') return;
          const el = document.elementFromPoint(e.position.x, e.position.y);
          if (!el) return;
          const inEditor = el.closest('.mp-editor') as HTMLElement | null;
          if (!inEditor) return;
          // Format relative to the active doc's directory so the saved
          // markdown stays portable.
          const doc = useDocStore.getState().activeDoc();
          const docPath = doc?.ref?.path ?? doc?.ref?.id ?? '';
          const docDir = docPath ? docPath.replace(/[\\/][^\\/]*$/, '') : '';
          const text = formatSourcesAsMarkdown(sources, docDir);
          if (!text) return;
          // Route to whichever editor was hit.
          if (inEditor.classList.contains('mp-wysiwyg')) {
            wysiwygHandleRef.current?.insertText(text, e.position.x, e.position.y);
          } else {
            const view = editorView;
            if (view) {
              const pos =
                view.posAtCoords({ x: e.position.x, y: e.position.y }) ??
                view.state.selection.main.head;
              view.dispatch({ changes: { from: pos, insert: text } });
              view.focus();
            }
          }
          // Clear shared drag state — we consumed the drop.
          useFsDragStore.getState().clearSources();
          return true;
        },
      })
      .then((u) => {
        if (cancelled) u();
        else unsub = u;
      })
      .catch((err) => console.debug('[slate] editor drop subscription failed', err));
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [platform, editorView]);

  // OS-driven opens ("Open With", double-click, CLI arg, or a forwarded second
  // instance). Routes each path EXACTLY like the drop handler: a folder is added
  // as a workspace root, a file is opened as a document. Covers both live opens
  // (event) and the cold-start buffered paths (drained inside the adapter).
  // Fully guarded — a single bad path never throws or aborts the rest.
  useEffect(() => {
    if (typeof platform.onOpenFiles !== 'function') return;
    let unsub: (() => void) | undefined;
    let cancelled = false;

    const handleOpenPaths = (paths: string[]): void => {
      void (async () => {
        for (const path of paths) {
          try {
            const isDir = (await platform.isDirectory?.(path)) ?? false;
            if (isDir) {
              const name = workspaceLabel(path);
              const ref = { id: path, path, name };
              addWorkspaceRoot(ref);
              void platform.pushRecentWorkspace(ref).catch(() => {});
            } else {
              // Routes on type: an image opens in the viewer, else as text.
              await openPath(platform, path);
            }
          } catch (err) {
            console.debug('[slate] open-path handling failed for', path, err);
          }
        }
      })();
    };

    void platform
      .onOpenFiles(handleOpenPaths)
      .then((u) => {
        if (cancelled) u();
        else unsub = u;
      })
      .catch((err) => console.debug('[slate] onOpenFiles registration failed', err));

    return () => {
      cancelled = true;
      try {
        unsub?.();
      } catch {
        /* ignore unsubscribe errors */
      }
    };
  }, [platform, addWorkspaceRoot]);

  // App-level keyboard shortcuts. We ONLY intercept genuine *app* commands
  // (New, Save, Open file, Open folder) so editor keys — undo/redo, bold/italic,
  // etc. — still reach the focused CodeMirror / Crepe instance. Attached/detached
  // in an effect and guarded for `window` so jsdom and SSR are safe.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKeyDown = (e: KeyboardEvent): void => {
      // Ctrl-Tab / Ctrl-Shift-Tab cycles tabs on every platform (browsers /
      // VSCode / iTerm — Cmd-Tab is owned by the OS for app switching on
      // macOS). When focus is inside the bottom tool panel, cycle TOOL tabs
      // (e.g. terminal-1 → terminal-2); otherwise cycle document tabs.
      if (e.key === 'Tab' && e.ctrlKey && !e.altKey && !e.metaKey) {
        const inTools =
          typeof document !== 'undefined' &&
          !!document.activeElement?.closest('[data-focus-zone="tools"]');
        if (inTools) {
          const tools = useToolsStore.getState();
          if (tools.instances.length < 2) return;
          e.preventDefault();
          e.stopPropagation();
          const cur = tools.instances.findIndex((i) => i.id === tools.activeInstanceId);
          const start = cur < 0 ? 0 : cur;
          const next = e.shiftKey
            ? (start - 1 + tools.instances.length) % tools.instances.length
            : (start + 1) % tools.instances.length;
          tools.setActiveInstance(tools.instances[next].id);
          return;
        }
        const store = useDocStore.getState();
        if (store.docs.length < 2) return;
        e.preventDefault();
        e.stopPropagation();
        const cur = store.docs.findIndex((d) => d.id === store.activeId);
        const start = cur < 0 ? 0 : cur;
        const next = e.shiftKey
          ? (start - 1 + store.docs.length) % store.docs.length
          : (start + 1) % store.docs.length;
        store.activateDoc(store.docs[next].id);
        return;
      }
      // Palette shortcuts — Cmd/Ctrl+K (no Shift), Cmd/Ctrl+Shift+P,
      // Cmd/Ctrl+Shift+Space, Cmd/Ctrl+/ (lands directly in command mode).
      // Handle BEFORE the general mod-check so editor keys after the early
      // return are unaffected, and so Shift+Mod+P/Space never fall through
      // into the Shift+Mod+O (open folder) branch below.
      if (modPressed(e) && !e.altKey) {
        const paletteKey = e.key.toLowerCase();
        const isK = !e.shiftKey && paletteKey === 'k';
        const isShiftP = e.shiftKey && paletteKey === 'p';
        const isShiftSpace = e.shiftKey && (paletteKey === ' ' || e.code === 'Space');
        const isSlash = !e.shiftKey && paletteKey === '/';
        if (isK || isShiftP || isShiftSpace || isSlash) {
          e.preventDefault();
          e.stopPropagation();
          const store = useDocStore.getState();
          // Cmd/Ctrl+/ is always the command-mode variant. Plain Cmd/Ctrl+K
          // follows the user's "Open palette in command mode" preference so
          // power users can make slash-first the default.
          const seedSlash = isSlash || (isK && store.paletteCommandModeDefault);
          if (seedSlash) {
            if (store.paletteOpen) store.setPaletteOpen(false);
            else store.openPaletteWithSeed('/');
          } else {
            store.togglePalette();
          }
          return;
        }
      }
      // View-mode switch: ⌃⌘1/2/3 on macOS, Ctrl+Shift+1/2/3 elsewhere. Placed
      // before the general mod-gate because macOS ⌃⌘ is intentionally NOT the
      // command modifier (modPressed treats ⌃⌘ as not-mod) and Ctrl+Shift+digit
      // would otherwise be swallowed by the shift block. Direct access: 1=plain,
      // 2=wysiwyg, 3=reading. Leaves plain ⌘1-3 free for the editors' heading keys.
      {
        const vd = e.key;
        const isViewSwitch = isMac()
          ? e.metaKey && e.ctrlKey && !e.altKey && !e.shiftKey
          : e.ctrlKey && e.shiftKey && !e.metaKey && !e.altKey;
        if (isViewSwitch && (vd === '1' || vd === '2' || vd === '3')) {
          const store = useDocStore.getState();
          const active = store.docs.find((d) => d.id === store.activeId);
          if (!active || active.kind === 'image') return; // image viewer has no view modes
          e.preventDefault();
          store.setViewMode(vd === '1' ? 'plaintext' : vd === '2' ? 'wysiwyg' : 'rendered');
          return;
        }
      }
      // Require the platform "command" modifier and never Alt — Alt opens up the
      // keyspace for editor/Crepe shortcuts we must not swallow.
      if (!modPressed(e) || e.altKey) return;
      const key = e.key.toLowerCase();
      // Shift is allowed ONLY for the explicit Shift+Mod+O (open folder) binding;
      // every other Shift+Mod combo belongs to the focused editor (e.g. redo).
      if (e.shiftKey) {
        if (key === 'o' && platform.capabilities.folders) {
          e.preventDefault();
          onEmptyOpenFolder();
        } else if (key === 'w' && platform.capabilities.folders) {
          // Cmd/Ctrl+Shift+W → confirm-and-close every open workspace folder.
          e.preventDefault();
          requestCloseAllWorkspaces();
        } else if (key === 'f' && !isTauriEnv()) {
          // Web only: the desktop window has its own native full-screen control.
          e.preventDefault();
          void (async () => {
            try {
              if (document.fullscreenElement) await document.exitFullscreen();
              else await document.documentElement.requestFullscreen();
            } catch (err) {
              console.debug('[slate] toggle fullscreen failed', err);
            }
          })();
        }
        return;
      }
      if (key === 'n') {
        e.preventDefault();
        newUntitled();
      } else if (key === 's') {
        e.preventDefault();
        void saveActiveDoc(platform);
      } else if (key === 'o') {
        e.preventDefault();
        void openFileToDoc(platform);
      } else if (key === 'w') {
        // Cmd/Ctrl-W closes the active tab. When the last tab is gone, fall
        // through to closing the window so a final Cmd-W quits the app.
        e.preventDefault();
        const store = useDocStore.getState();
        if (store.activeId) {
          store.requestCloseDocs([store.activeId]);
        } else if (typeof platform.closeWindow === 'function') {
          void platform.closeWindow().catch(() => {
            /* ignore — web may forbid programmatic close */
          });
        }
      } else if (key === '=' || e.key === '+' || key === '-' || key === '0') {
        // Reading-mode text zoom (Cmd/Ctrl-+ / − / 0). Only intercept when
        // the user is actually in reading mode — otherwise leave the keys
        // for the editor (where mod-0 has no meaning, but +/− shouldn't get
        // swallowed in case they get bound later).
        const store = useDocStore.getState();
        if (store.viewMode !== 'rendered') return;
        e.preventDefault();
        if (key === '0') store.resetReadingFontScale();
        else if (key === '-') store.nudgeReadingFontScale(-0.1);
        else store.nudgeReadingFontScale(0.1);
      } else if (key === '\\') {
        // ⌘\ toggles split view (matches VS Code).
        const store = useDocStore.getState();
        const active = store.docs.find((d) => d.id === store.activeId);
        if (!active || active.kind === 'image') return;
        e.preventDefault();
        store.toggleSplit();
      } else if (key === ',') {
        // ⌘, opens Settings (macOS convention, also bound on Win/Lin).
        // SettingsDialog state lives in TitleBar; dispatch the same window
        // event the `/settings` palette command uses.
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('slate:open-settings'));
      }
      // Mod+Z/Y/B/I/etc. are deliberately NOT handled here — they belong to the
      // focused editor.
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [platform, newUntitled, onEmptyOpenFolder, requestCloseAllWorkspaces]);

  // External-change watchers (Task 35). One per OPEN saved file. The dep is a
  // STABLE key over the SET of watchable refs (id + path/id) — NOT doc text —
  // so typing never re-subscribes. When the file changes on disk: clean buffer
  // → silently reload; dirty buffer → queue a conflict for the modal. We read
  // LIVE state via getState() so the callback never sees stale dirty/text.
  const watchKey = useMemo(
    () =>
      docs
        .filter((d) => d.ref)
        .map((d) => `${d.id}|${d.ref?.path ?? d.ref?.id ?? ''}`)
        .join('\n'),
    [docs],
  );

  useEffect(() => {
    if (!platform.capabilities.fileWatch) return;
    const store = useDocStore.getState();
    const watchable = store.docs.filter((d) => d.ref);
    if (watchable.length === 0) return;

    const unsubs = new Map<string, () => void>();
    let cancelled = false;

    for (const doc of watchable) {
      const id = doc.id;
      const ref = doc.ref;
      if (!ref) continue;
      const onEvent = (e: WatchEvent): void => {
        void (async () => {
          try {
            const live = useDocStore.getState().docs.find((d) => d.id === id);
            if (!live || !live.ref) return;
            if (e.kind === 'remove') {
              // Conservative: surface only if there are unsaved edits to lose.
              if (live.dirty) useDocStore.getState().enqueueConflict(id);
              return;
            }
            // 'modify' | 'rename'
            if (live.dirty) {
              useDocStore.getState().enqueueConflict(id);
            } else {
              const { content } = await platform.readFile(live.ref);
              useDocStore.getState().applyExternalReload(id, content);
            }
          } catch (err) {
            console.debug('[slate] watch event handling failed', id, err);
          }
        })();
      };
      void platform
        .watchFile(ref, onEvent)
        .then((unsub) => {
          // The effect may have torn down before this resolved.
          if (cancelled) unsub();
          else unsubs.set(id, unsub);
        })
        .catch((err) => console.debug('[slate] watchFile failed', id, err));
    }

    return () => {
      cancelled = true;
      for (const unsub of unsubs.values()) {
        try {
          unsub();
        } catch {
          /* ignore unsubscribe errors */
        }
      }
      unsubs.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watchKey encodes
    // the watchable ref SET; depending on `docs` would re-subscribe on edits.
  }, [platform, watchKey]);

  // Conflict resolution: the modal targets conflicts[0]. Handlers read live
  // state for the conflicted doc and call the platform per the behavior spec.
  const conflictId = conflicts[0] ?? null;
  const conflictDoc = conflictId ? docs.find((d) => d.id === conflictId) : undefined;

  // If the conflicted doc vanished (e.g. closed), drop it from the queue.
  useEffect(() => {
    if (conflictId && !conflictDoc) {
      useDocStore.getState().resolveConflict(conflictId);
    }
  }, [conflictId, conflictDoc]);

  const dismissConflict = useCallback(() => {
    if (conflictId) useDocStore.getState().resolveConflict(conflictId);
  }, [conflictId]);

  const onConflictKeepMine = useCallback(() => {
    if (!conflictId) return;
    void (async () => {
      try {
        const doc = useDocStore.getState().docs.find((d) => d.id === conflictId);
        if (!doc?.ref) return;
        await platform.writeFile(doc.ref, doc.text);
        useDocStore.getState().markDocClean(conflictId);
      } catch (err) {
        console.debug('[slate] conflict keep-mine failed', err);
      } finally {
        useDocStore.getState().resolveConflict(conflictId);
      }
    })();
  }, [conflictId, platform]);

  const onConflictLoadExternal = useCallback(() => {
    if (!conflictId) return;
    void (async () => {
      try {
        const doc = useDocStore.getState().docs.find((d) => d.id === conflictId);
        if (!doc?.ref) return;
        const { content } = await platform.readFile(doc.ref);
        useDocStore.getState().applyExternalReload(conflictId, content);
      } catch (err) {
        console.debug('[slate] conflict load-external failed', err);
      } finally {
        useDocStore.getState().resolveConflict(conflictId);
      }
    })();
  }, [conflictId, platform]);

  const onConflictSaveAs = useCallback(() => {
    if (!conflictId) return;
    void (async () => {
      try {
        const doc = useDocStore.getState().docs.find((d) => d.id === conflictId);
        if (!doc?.ref) return;
        const newPath = await platform.saveFileDialog({ defaultPath: doc.ref.name });
        // Cancelled → leave the conflict unresolved (modal stays).
        if (!newPath) return;
        await platform.writeFile(newPath, doc.text);
        const name = newPath.split(/[\\/]/).pop() ?? doc.ref.name;
        // Open the user's edits as a NEW doc/tab (preserved under a new name).
        useDocStore.getState().openDoc({ id: newPath, path: newPath, name }, doc.text);
        // Reload the ORIGINAL doc from disk so its tab shows the external content.
        const { content } = await platform.readFile(doc.ref);
        useDocStore.getState().applyExternalReload(conflictId, content);
        useDocStore.getState().resolveConflict(conflictId);
      } catch (err) {
        console.debug('[slate] conflict save-as failed', err);
      }
    })();
  }, [conflictId, platform]);

  // --- Unsaved-changes guards (Task 55) ---------------------------------------

  // Tab-close guard. The TitleBar enqueues ids (X / Close / Close others / Close
  // all); we drain the queue here. Clean docs close immediately; the FIRST dirty
  // one drives a Save/Don't-save/Cancel dialog. We process one dirty doc at a
  // time so multi-close stays correct and never janky.
  const closeTargetId = closeQueue.find((id) => docs.find((d) => d.id === id)?.dirty) ?? null;
  const closeTargetDoc = closeTargetId ? docs.find((d) => d.id === closeTargetId) : undefined;

  useEffect(() => {
    if (closeQueue.length === 0) return;
    const store = useDocStore.getState();
    // Close every queued doc that is clean (or already gone) right away. The
    // remaining dirty docs stay queued and surface in the dialog, one at a time.
    for (const id of closeQueue) {
      const doc = store.docs.find((d) => d.id === id);
      if (!doc) {
        store.dequeueClose(id);
      } else if (!doc.dirty) {
        store.closeDoc(id); // closeDoc also dequeues
      }
    }
  }, [closeQueue]);

  const onCloseTabSave = useCallback(() => {
    if (!closeTargetId) return;
    const id = closeTargetId;
    void (async () => {
      try {
        const saved = await saveDocById(platform, id);
        // Saving an untitled buffer remaps its id (markSaved); the doc we asked
        // to close may now live under a new id. Dequeue the original id and
        // close whichever id is now clean.
        useDocStore.getState().dequeueClose(id);
        if (saved) {
          // The doc is clean — close it (matching by original id; if remapped,
          // the original id no longer exists so this is a safe no-op and the
          // buffer is preserved under its new saved name/tab).
          useDocStore.getState().closeDoc(id);
        }
        // Cancelled Save As → keep the doc open (already dequeued so we don't loop).
      } catch (err) {
        console.debug('[slate] close-tab save failed', err);
        useDocStore.getState().dequeueClose(id);
      }
    })();
  }, [closeTargetId, platform]);

  const onCloseTabDontSave = useCallback(() => {
    if (!closeTargetId) return;
    useDocStore.getState().closeDoc(closeTargetId); // discards + dequeues
  }, [closeTargetId]);

  const onCloseTabCancel = useCallback(() => {
    // Cancel aborts the WHOLE pending close request (e.g. a Close-all).
    useDocStore.getState().clearCloseQueue();
  }, []);

  // Window-close guard. The adapter prevents the default close and calls our
  // handler; we decide whether to prompt. Registered once on mount.
  const [windowClosePrompt, setWindowClosePrompt] = useState(false);
  // Busy terminals captured at close-request time, surfaced in the dialog so
  // the user can see which running processes a "close anyway" would SIGHUP.
  const [windowBusyTerminals, setWindowBusyTerminals] = useState<BusyTerminal[]>([]);

  // Launch-recovery picker: file-backed tabs whose unsaved draft differs from
  // the current on-disk content. Populated by the bootstrap restore effect.
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryItems, setRecoveryItems] = useState<Array<{ id: string; name: string; path: string }>>([]);
  const recoveryDraftsRef = useRef<Record<string, { draftText: string; draftId?: string }>>({});

  const onRecoveryResolve = useCallback((decisions: Record<string, RecoveryChoice>) => {
    setRecoveryOpen(false);
    for (const [docId, choice] of Object.entries(decisions)) {
      const entry = recoveryDraftsRef.current[docId];
      if (!entry) continue;
      if (choice === 'restore') {
        // Swap the open tab's text to the draft and mark dirty.
        useDocStore.setState((s) => ({
          docs: s.docs.map((d) =>
            d.id === docId ? { ...d, text: entry.draftText, dirty: true, lastEditor: 'none' } : d,
          ),
        }));
        if (entry.draftId) draftRegistry.assign(docId, entry.draftId);
      } else {
        // Skip: the disk version is already open; drop the draft.
        if (entry.draftId) void platform.session?.deleteDraft(entry.draftId).catch(() => {});
      }
    }
    recoveryDraftsRef.current = {};
  }, [platform]);

  // A promise resolver the confirm dialog fulfils. attemptClose awaits it.
  const busyConfirmResolverRef = useRef<((proceed: boolean) => void) | null>(null);
  const confirmBusy = useCallback(
    (busy: BusyTerminalInfo[]): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        if (busyConfirmResolverRef.current) {
          resolve(false); // a prompt is already open; don't stack
          return;
        }
        setWindowBusyTerminals(busy);
        setWindowClosePrompt(true);
        busyConfirmResolverRef.current = resolve;
      }),
    [],
  );

  useEffect(() => {
    let unsubs: Array<() => void> = [];
    let cancelled = false;
    const deps = { platform, toolHost, confirmBusy };
    const wire = async () => {
      if (typeof platform.onCloseRequested === 'function') {
        const u = await platform.onCloseRequested(() => {
          void attemptClose('window', deps);
        });
        if (cancelled) u();
        else unsubs.push(u);
      }
      if (typeof platform.onQuitRequested === 'function') {
        const u = await platform.onQuitRequested(() => {
          void attemptClose('app', deps);
        });
        if (cancelled) u();
        else unsubs.push(u);
      }
    };
    void wire().catch((err) => console.debug('[slate] close wiring failed', err));
    return () => {
      cancelled = true;
      for (const u of unsubs) {
        try {
          u();
        } catch {
          /* ignore */
        }
      }
      unsubs = [];
    };
  }, [platform, toolHost, confirmBusy]);

  // Pending jump-to-line: a content-search match click opens the file then sets
  // pendingJumpLine. Once the relevant view has mounted with the new doc, scroll
  // it to the line and clear the request. Depends on activeId + the captured
  // view/scroller refs so it re-runs after the editor/preview remounts. A rAF
  // gives the new instance a frame to lay out before we measure. Fully guarded.
  useEffect(() => {
    if (pendingJumpLine == null) return;
    const line = pendingJumpLine;

    // Which view(s), if any, can honour a source-line jump in the current mode.
    const wantsEditor = editorVisible && viewMode !== 'wysiwyg';
    const wantsPreview = previewVisible;
    const editorReady = !wantsEditor || editorView !== null;
    const previewReady = !wantsPreview || previewScroller !== null;
    // WYSIWYG-only (no preview) has no line mapping → resolve immediately.
    const noLineTarget = !wantsEditor && !wantsPreview;

    // Wait until the relevant view has mounted; the effect re-runs when the
    // captured view/scroller refs update so a freshly-opened doc still lands.
    if (!noLineTarget && (!editorReady || !previewReady)) return;

    const raf = requestAnimationFrame(() => {
      try {
        if (wantsEditor && editorView) {
          // Edit mode: place the caret at the line AND scroll, so the user can
          // resume typing right where the search match was. Reading-only mode
          // (preview side) just scrolls — there's no caret to place.
          placeCursorAtLine(editorView, line);
        }
        if (wantsPreview) previewHandleRef.current?.scrollToLine(line);
      } catch {
        /* never throw out of an effect */
      }
      setPendingJumpLine(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [
    activeId,
    pendingJumpLine,
    editorView,
    previewScroller,
    editorVisible,
    previewVisible,
    viewMode,
    setPendingJumpLine,
  ]);

  // --- About / Acknowledgements dialog ----------------------------------------
  const [aboutOpen, setAboutOpen] = useState(false);
  const [aboutShowAck, setAboutShowAck] = useState(false);
  const [appVersion, setAppVersion] = useState<string | undefined>(undefined);
  const [updateGateOpen, setUpdateGateOpen] = useState(false);
  const [updateGateDirty, setUpdateGateDirty] = useState(0);

  // Native application-menu actions (desktop): "about"/"acknowledgements" open
  // the in-app dialog. No-op where there's no native menu (web omits onMenuAction).
  useEffect(() => {
    if (typeof platform.onMenuAction !== 'function') return;
    let unsub: (() => void) | undefined;
    let cancelled = false;
    void platform
      .onMenuAction((action) => {
        if (action === 'about' || action === 'acknowledgements') {
          setAboutShowAck(action === 'acknowledgements');
          setAboutOpen(true);
        }
      })
      .then((u) => {
        if (cancelled) u();
        else unsub = u;
      })
      .catch((err) => console.debug('[slate] onMenuAction registration failed', err));
    return () => {
      cancelled = true;
      try {
        unsub?.();
      } catch {
        /* ignore unsubscribe errors */
      }
    };
  }, [platform]);

  // The `/about` palette command emits a window-level `slate:open-about`
  // CustomEvent (the palette has no React handle on this state). Listen for it
  // here so the slash command opens the in-app dialog. `setAboutOpen` is a
  // stable React setter, so the empty dep array is correct.
  useEffect(() => {
    const onOpenAbout = (e: Event): void => {
      // The logo menu's About/Acknowledgements items carry an `ack` flag.
      const ack = (e as CustomEvent<{ ack?: boolean }>).detail?.ack === true;
      setAboutShowAck(ack);
      setAboutOpen(true);
    };
    const onCheckUpdates = (): void => {
      void useUpdateStore.getState().checkNow({ mode: 'manual' });
    };
    const onCloseAllWorkspaces = (): void => requestCloseAllWorkspaces();
    window.addEventListener('slate:open-about', onOpenAbout);
    window.addEventListener('slate:check-updates', onCheckUpdates);
    window.addEventListener('slate:close-all-workspaces', onCloseAllWorkspaces);
    return () => {
      window.removeEventListener('slate:open-about', onOpenAbout);
      window.removeEventListener('slate:check-updates', onCheckUpdates);
      window.removeEventListener('slate:close-all-workspaces', onCloseAllWorkspaces);
    };
  }, [requestCloseAllWorkspaces]);

  // Register the built-in sidebar panels (Explorer) once. Adding more panels
  // later is a one-line change in registerBuiltinSidebarPanels.
  useEffect(() => registerBuiltinSidebarPanels(platform, toolHost), [platform, toolHost]);

  // The Plugins panel (activity-bar icon + body) hosting installed plugins.
  useEffect(() => registerPluginsSidebarPanel(platform), [platform]);
  // Surface every installed plugin's activity-bar items as sidebar panels.
  useEffect(() => registerPluginContributedPanels(getAppPluginSystem(platform)), [platform]);
  // Surface every installed plugin's contributed tools in the bottom panel.
  useEffect(() => registerPluginContributedTools(getAppPluginSystem(platform)), [platform]);

  // The immersive Git client main view, registered as an activity-bar item.
  // Same gating as the Source Control panel above: desktop-only AND the slate.git
  // plugin installed + enabled. When the plugin is disabled while the Git client
  // is open, close it back to the editor so the activity-bar item and the view
  // disappear together (no orphaned, empty main area).
  useEffect(() => {
    if (!platform.capabilities.git) return;
    const host = getAppPluginSystem(platform).host;
    let dispose: (() => void) | null = null;
    const sync = (): void => {
      const enabled =
        host.list().some((p) => p.manifest.id === GIT_PLUGIN_ID) && host.isEnabled(GIT_PLUGIN_ID);
      if (enabled && !dispose) {
        dispose = registerGitMainView(platform);
      } else if (!enabled && dispose) {
        dispose();
        dispose = null;
        const sidebar = useSidebarStore.getState();
        if (sidebar.activeMainView === 'git') sidebar.setActiveMainView(null);
      }
    };
    sync();
    const unsub = host.onDidChangeContributions(sync);
    return () => {
      unsub();
      dispose?.();
      dispose = null;
    };
  }, [platform]);
  // The plugin host, so installed plugins' commands surface in the palette. The
  // accessor caches one system per platform, so this reference stays stable.
  const pluginHost = useMemo(() => getAppPluginSystem(platform).host, [platform]);

  // Dev-only: preview the "update available" toast ~3s after launch so its
  // design can be iterated on without cutting a real release. Skipped in tests
  // (MODE === 'test') and in production builds (DEV !== true).
  useEffect(() => {
    const env = (import.meta as unknown as { env?: { DEV?: boolean; MODE?: string } }).env;
    if (env?.DEV !== true || env.MODE === 'test') return;
    const timer = window.setTimeout(() => {
      let id = 0;
      id = useToastStore.getState().push("Slate v0.5.1 is available. You're on v0.4.4.", {
        sticky: true,
        actions: [
          {
            label: 'Update now',
            primary: true,
            onClick: () => {
              useToastStore.getState().dismiss(id);
              window.dispatchEvent(new CustomEvent(START_UPDATE_EVENT));
            },
          },
          { label: 'Later', onClick: () => useToastStore.getState().dismiss(id) },
        ],
      });
    }, 3000);
    return () => window.clearTimeout(timer);
  }, []);

  // Resolve the app version lazily the first time the dialog opens.
  useEffect(() => {
    if (
      !aboutOpen ||
      appVersion !== undefined ||
      typeof platform.getAppVersion !== 'function'
    ) {
      return;
    }
    let cancelled = false;
    void platform
      .getAppVersion()
      .then((v) => {
        if (!cancelled) setAppVersion(v);
      })
      .catch((err) => console.debug('[slate] getAppVersion failed', err));
    return () => {
      cancelled = true;
    };
  }, [aboutOpen, appVersion, platform]);

  // --- In-app updater wiring --------------------------------------------------
  // Populate the update store's `currentVersion` once on mount so Settings and
  // About show the version even on platforms where the boot probe (Tauri-only)
  // never runs. No-op when the bridge lacks `getAppVersion` (web).
  useEffect(() => {
    void useUpdateStore.getState().loadCurrentVersion(platform);
  }, [platform]);

  // The sticky "update available" toast's `Update now` action dispatches
  // START_UPDATE_EVENT (it has no PlatformBridge handle); `requestInstall`
  // dispatches OPEN_UPDATE_GATE_EVENT when there are dirty docs to save first.
  useEffect(() => {
    const onStart = (): void => {
      void useUpdateStore.getState().requestInstall(platform);
    };
    const onGate = (e: Event): void => {
      const detail = (e as CustomEvent<{ dirty: number }>).detail;
      setUpdateGateDirty(detail?.dirty ?? 0);
      setUpdateGateOpen(true);
    };
    window.addEventListener(START_UPDATE_EVENT, onStart);
    window.addEventListener(OPEN_UPDATE_GATE_EVENT, onGate);
    return () => {
      window.removeEventListener(START_UPDATE_EVENT, onStart);
      window.removeEventListener(OPEN_UPDATE_GATE_EVENT, onGate);
    };
  }, [platform]);

  const onUpdateGateConfirm = useCallback(() => {
    setUpdateGateOpen(false);
    void (async () => {
      try {
        const allSaved = await saveAllDocs(platform);
        if (!allSaved) {
          toast('Update cancelled. Some changes were not saved.', {
            kind: 'info',
          });
          return;
        }
        await useUpdateStore.getState().confirmInstall();
      } catch (err) {
        console.debug('[slate] update save-all failed', err);
      }
    })();
  }, [platform]);

  const onUpdateGateCancel = useCallback(() => {
    setUpdateGateOpen(false);
  }, []);

  const openExternal = useCallback(
    (url: string): void => {
      void platform.openExternal(url).catch((err) =>
        console.debug('[slate] openExternal failed', url, err),
      );
    },
    [platform],
  );

  const plainEditor = (
    <PlainTextEditor
      value={text}
      dark={dark}
      onChange={(v) => setText(v, 'plaintext')}
      onScroller={setEditorScroller}
      onView={setEditorView}
      fileName={effectiveName ?? undefined}
      markdown={!editorOnly}
      softWrap={softWrap}
      foldRanges={foldRanges}
      foldingEnabled={foldingEnabled}
      onToggleWrap={() => {
        if (activeId) useDocStore.getState().toggleDocWrap(activeId);
      }}
    />
  );

  // For code/text files only the plain-text editor is available, regardless of
  // the (shared) store viewMode/split. Markdown/untitled keep the full views.
  //
  // Why `key={activeId}` on WysiwygEditor: Crepe is mount-once (spec §3.2 —
  // single live writer; external `value` changes are NOT pushed into a live
  // instance). Switching the active doc/tab must therefore remount the
  // editor so its display + its onChange closure both bind to the new doc.
  // Without this the WYSIWYG view keeps showing the previous file and
  // subsequent edits land on whichever doc is *now* active (data loss). The
  // plaintext editor reactively syncs `value` and doesn't need a key.
  // Resolver shared by WYSIWYG + rendered preview so relative image srcs
  // (`./foo.svg`) load against the active doc's directory through the
  // platform's asset protocol. Declared before the editor / preview JSX so
  // both can reference it.
  const docPathForAssets = activeDoc?.ref?.path ?? activeDoc?.ref?.id ?? '';
  const resolveImageSrc = (src: string): string => {
    if (!docPathForAssets) return src;
    try {
      return platform.resolveAssetUrl(docPathForAssets, src);
    } catch {
      return src;
    }
  };
  const editor = (
    <EditorContextArea platform={platform}>
      {!editorOnly && viewMode === 'wysiwyg' ? (
        <Suspense fallback={<div className="mp-placeholder">Loading editor…</div>}>
          <WysiwygEditor
            key={activeId ?? '__none__'}
            ref={wysiwygHandleRef}
            value={text}
            onChange={(v) => setText(v, 'wysiwyg')}
            onScroller={setEditorScroller}
            onResolveImageSrc={resolveImageSrc}
          />
        </Suspense>
      ) : (
        plainEditor
      )}
    </EditorContextArea>
  );
  // A RenderedPreview factory: the FULL rendered/Reading view is ALWAYS reading
  // (regardless of the split flag — selecting Reading should show reading
  // styling). The split right-pane preview keeps Crepe parity (reading=false)
  // so editor + preview overlay 1:1.
  const makePreview = (reading: boolean): ReactElement => (
    <RenderedPreview
      ref={previewHandleRef}
      text={text}
      scrollRef={setPreviewScroller}
      reading={reading}
      readingFontScale={reading ? readingFontScale : undefined}
      readingWidth={reading ? readingWidth : undefined}
      onLink={handleLink}
      onCopyText={copyText}
      onResolveImageSrc={resolveImageSrc}
      onImageClick={(src, alt) => setLightbox({ src, name: alt })}
      onEmbedClick={(src, kind) =>
        setLightbox({
          src,
          name: kind === 'mermaid' ? 'Mermaid diagram' : 'Chart',
        })
      }
    />
  );

  let body: ReactElement;
  if (activeMainViewItem) {
    // A plugin main view (e.g. the Git client) takes over the entire editor
    // area: no toolbar, no outline, no editor/preview. Highest priority so it
    // wins even over the welcome screen.
    const MainView = activeMainViewItem.Component;
    body = (
      <div className="mp-pane">
        <MainView />
      </div>
    );
  } else if (docs.length === 0) {
    // No open documents → welcome screen with recent files + workspace folders.
    // Two-column layout (collapses to one column under ~640px) anchored by a
    // header (brand) and a footer (primary actions + palette hints).
    body = (
      <div className="mp-empty mp-welcome">
        <div className="mp-welcome__inner">
          <header className="mp-welcome__hero">
            <p className="mp-welcome__brand">Slate</p>
            <p className="mp-welcome__tagline">Open a file, pick up where you left off,
              {' '}or press <span className="mp-kbd">{shortcutLabel('/')}</span> to run a command.</p>
          </header>

          <div className="mp-welcome__cols">
            <section className="mp-welcome__col" aria-labelledby="welcome-recents-h">
              <h3 id="welcome-recents-h" className="mp-welcome__col-h">
                <Clock size={13} aria-hidden /> Recent files
              </h3>
              <RecentsList
                items={recentFiles.map((f) => ({
                  id: f.ref.id,
                  name: f.ref.name,
                  path: f.ref.path ?? '',
                }))}
                icon="file"
                emptyMessage="No recent files yet."
                onOpen={onOpenRecentFile}
                onRemove={onRemoveRecentFile}
              />
            </section>

            <section className="mp-welcome__col" aria-labelledby="welcome-roots-h">
              <h3 id="welcome-roots-h" className="mp-welcome__col-h">
                <Folder size={13} aria-hidden /> Recent workspaces
              </h3>
              <RecentsList
                items={recentWorkspaces.map((ws) => ({
                  id: ws.ref.id,
                  name: ws.ref.name,
                  path: ws.ref.path ?? '',
                }))}
                icon="folder"
                emptyMessage={
                  platform.capabilities.folders
                    ? 'No recent workspaces yet. Add one to browse it in the sidebar.'
                    : 'Folder access is not available here.'
                }
                onOpen={onOpenRecentWorkspace}
                onRemove={onRemoveRecentWorkspace}
              />
            </section>
          </div>

          <p className="mp-welcome__tip" role="note" aria-live="polite">
            <span className="mp-welcome__tip-label">Tip</span>
            <span className="mp-welcome__tip-body">{welcomeTip}</span>
          </p>

          <footer className="mp-welcome__footer">
            <div className="mp-welcome__actions">
              <button type="button" className="mp-btn mp-btn--primary" onClick={onEmptyNewFile}>
                <FilePlus size={15} aria-hidden />
                New file
                <span className="mp-kbd">{shortcutLabel('N')}</span>
              </button>
              <button type="button" className="mp-btn" onClick={onEmptyOpenFile}>
                <FileUp size={15} aria-hidden />
                Open file
                <span className="mp-kbd">{shortcutLabel('O')}</span>
              </button>
              {platform.capabilities.folders && (
                <button type="button" className="mp-btn" onClick={onEmptyOpenFolder}>
                  <FolderOpen size={15} aria-hidden />
                  Open folder
                  <span className="mp-kbd">{shortcutLabel('O', { shift: true })}</span>
                </button>
              )}
            </div>
            <p className="mp-welcome__hint">
              <span className="mp-kbd">{shortcutLabel('K')}</span> search ·{' '}
              <span className="mp-kbd">{shortcutLabel('/')}</span> slash commands
              {!isTauriEnv() && (
                <>
                  {' · '}
                  <span className="mp-kbd">{shortcutLabel('F', { shift: true })}</span> full screen
                </>
              )}
            </p>
          </footer>
        </div>
      </div>
    );
  } else if (isViewerOnly && activeDoc?.ref) {
    // Image file: a read-only viewer is the ONLY view (no editor/preview/split).
    body = (
      <div className="mp-pane">
        <ImageViewer platform={platform} refOrPath={activeDoc.ref} />
      </div>
    );
  } else if (editorOnly) {
    // Code/text file: the plain-text editor is the ONLY view (ignore split/mode).
    body = <div className="mp-pane">{editor}</div>;
  } else if (viewMode === 'rendered') {
    body = (
      <div className="mp-pane mp-pane--reading">
        {makePreview(true)}
        <ReadingControls />
      </div>
    );
  } else if (split) {
    body = <SplitView left={editor} right={makePreview(false)} />;
  } else {
    body = <div className="mp-pane">{editor}</div>;
  }

  const outlineOpen = outlinePinned || outlineRevealed;

  return (
    <ContextMenuProvider>
    <div className="mp-app">
      {/* Full-width header across the entire top: logo menu + tabs + controls. */}
      <TitleBar
        reserveTrafficLights={trafficLights}
        showOutlineToggle={hasMarkdownDoc || hasCodeOutline}
        outlinePinned={outlinePinned}
        onToggleOutline={handleToggleOutline}
        platform={platform}
      />
      <div className="mp-shell">
        {/* Thin, always-visible activity bar: panel switchers + file actions +
            bottom-pinned collapse toggle. */}
        <ActivityBar
          onNew={sidebarActions.onNew}
          onOpenFile={() => void sidebarActions.onOpenFile()}
          onOpenFolder={() => void sidebarActions.onOpenFolder()}
        />
        {/* Expandable sidebar — renders the active registered panel, or nothing
            when collapsed. A main view (e.g. Git) is mutually exclusive with the
            sidebar, so it is hidden entirely while one is active. */}
        {!mainViewActive && <SidebarHost />}
        {/* Center column: toolbar, content, bottom tool dock. */}
        <div
          className="mp-workspace"
          data-outline-pinned={
            (hasMarkdownDoc || hasCodeOutline) && outlinePinned && !mainViewActive ? '' : undefined
          }
        >
          {/* Image docs are read-only: the toolbar (incl. Save) is hidden
              entirely, like the empty state. A plugin main view also takes over
              the whole area, so it hides the toolbar too. Other docs keep it. */}
          {hasDoc && !isViewerOnly && !mainViewActive && (
            <Toolbar platform={platform} editorOnly={editorOnly} pluginHost={pluginHost} />
          )}
          <div className="mp-main" data-focus-zone="editor" ref={editorZoneRef}>
            {body}
          </div>
          {/* Bottom tool dock (terminal etc.) — docks below the editor area
              inside the workspace column. Renders null while hidden. */}
          <ToolPanel platform={platform} host={toolHost} />
        </div>

        {/* Right-edge outline — markdown docs show the heading outline; supported
            code files show a symbol outline. Empty/image/unsupported docs have
            none, and a plugin main view hides it too. Pinned → docked column;
            unpinned → hover overlay revealed by the thin edge zone. */}
        {(hasMarkdownDoc || hasCodeOutline) && !mainViewActive && (
          <>
            {!outlinePinned && (
              <div
                className="mp-outline__edge"
                aria-hidden
                onMouseEnter={() => setOutlineRevealed(true)}
              />
            )}
            <aside
              className="mp-outline"
              data-pinned={outlinePinned ? '' : undefined}
              data-open={outlineOpen ? '' : undefined}
              onMouseEnter={() => {
                if (!outlinePinned) setOutlineRevealed(true);
              }}
              onMouseLeave={() => {
                if (!outlinePinned) setOutlineRevealed(false);
              }}
            >
              {hasMarkdownDoc ? (
                <OutlinePanel
                  headings={headings}
                  pinned={outlinePinned}
                  onTogglePin={handleToggleOutline}
                  onJump={jumpToHeading}
                  activeSlug={activeHeadingSlug}
                />
              ) : (
                <CodeOutlinePanel
                  symbols={codeSymbols}
                  pinned={outlinePinned}
                  onTogglePin={handleToggleOutline}
                  onJump={jumpToSymbol}
                />
              )}
            </aside>
          </>
        )}
      </div>

      {conflictDoc && (
        <ConflictDialog
          open
          fileName={conflictDoc.ref?.name ?? conflictDoc.id}
          onKeepMine={onConflictKeepMine}
          onLoadExternal={onConflictLoadExternal}
          onSaveAs={onConflictSaveAs}
          onClose={dismissConflict}
        />
      )}

      {closeTargetDoc && (
        <CloseConfirmDialog
          open
          variant="tab"
          fileName={closeTargetDoc.ref?.name ?? 'Untitled'}
          onSave={onCloseTabSave}
          onDontSave={onCloseTabDontSave}
          onCancel={onCloseTabCancel}
        />
      )}

      <CloseConfirmDialog
        open={windowClosePrompt}
        variant="window"
        dirtyCount={0}
        busyTerminals={windowBusyTerminals}
        onCloseAnyway={() => {
          setWindowClosePrompt(false);
          busyConfirmResolverRef.current?.(true);
          busyConfirmResolverRef.current = null;
        }}
        onCancel={() => {
          setWindowClosePrompt(false);
          busyConfirmResolverRef.current?.(false);
          busyConfirmResolverRef.current = null;
        }}
        onSave={() => {
          // No dirty-doc branch in the quit guard anymore; treat Save as proceed.
          setWindowClosePrompt(false);
          busyConfirmResolverRef.current?.(true);
          busyConfirmResolverRef.current = null;
        }}
        onDontSave={() => {
          setWindowClosePrompt(false);
          busyConfirmResolverRef.current?.(true);
          busyConfirmResolverRef.current = null;
        }}
      />

      <RecoveryDialog open={recoveryOpen} items={recoveryItems} onResolve={onRecoveryResolve} />

      <CloseAllWorkspacesDialog
        open={closeAllWsOpen}
        roots={workspaceRoots}
        onConfirm={confirmCloseAllWorkspaces}
        onCancel={() => setCloseAllWsOpen(false)}
      />

      <AboutDialog
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        version={appVersion}
        onOpenExternal={openExternal}
        showAcknowledgements={aboutShowAck}
      />

      <UpdateConfirmDialog
        open={updateGateOpen}
        dirtyCount={updateGateDirty}
        onConfirm={onUpdateGateConfirm}
        onCancel={onUpdateGateCancel}
      />
      <UpdateInstallModal />

      <ToastHost />

      {lightbox && (
        <ImageLightbox
          platform={platform}
          src={lightbox.src}
          name={lightbox.name}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Drop-target caret preview — painted while a sidebar drag hovers an
          editor so the user sees where the markdown link/image will land. */}
      {dropCaret && (
        <div
          className="mp-drop-caret"
          aria-hidden
          style={{
            left: dropCaret.left,
            top: dropCaret.top,
            height: Math.max(8, dropCaret.bottom - dropCaret.top),
          }}
        />
      )}

      {/* Command palette — mounted once at the App root; renders null when
          closed. Opened via Cmd/Ctrl+K, Cmd/Ctrl+Shift+P, Cmd/Ctrl+Shift+Space,
          or the TitleBar icon (all flip the same docStore.paletteOpen flag). */}
      <CommandPalette platform={platform} toolHost={toolHost} pluginHost={pluginHost} />
    </div>
    </ContextMenuProvider>
  );
}
